import type {
  GetStaticPathsContext,
  GetStaticPropsContext,
  InferGetStaticPropsType,
  GetServerSidePropsContext,
} from "next"
import { useRouter } from "next/router"
import { Layout } from "@components/common"
import { BuilderComponent, Builder, builder } from "@builder.io/react"
import { resolveSwellContent } from "@lib/resolve-swell-content"
import builderConfig from "@config/builder"
import {
  getProductsByCollection,
  getAllCollectionPaths,
  getAllCollections,
} from "@lib/swell/storefront-data-hooks/src/api/operations-swell"
import DefaultErrorPage from "next/error"
import Head from "next/head"
import { useThemeUI } from "@theme-ui/core"
import { getLayoutProps } from "@lib/get-layout-props"
import { classNames } from "core/utils"
import Image from "next/image"
Builder.isStatic = true

const builderModel = "collection-page"

export async function getStaticProps({
  params,
}: GetStaticPropsContext<{ handle: string; page: string }>) {
  const collection = await getProductsByCollection(builderConfig, {
    handle: params?.handle,
    productsQuery: {
      limit: 15,
      page: params?.page,
    },
  })

  const collections = await getAllCollections(builderConfig)

  const page = await resolveSwellContent(builderModel, {
    collectionHandle: params?.handle,
  })

  return {
    props: {
      page: page || null,
      collection: collection || null,
      category: collection.category || null,
      collections: collections || null,
      ...(await getLayoutProps()),
    },
    // Next.js will attempt to re-generate the page:
    // - When a request comes in
    // - At most once every 30 seconds
    revalidate: 30,
  }
}

export async function getStaticPaths({ locales }: GetStaticPathsContext) {
  const paths = await getAllCollectionPaths(builderConfig)
  return {
    paths: paths.map((path) => `/collection/${path}`),
    fallback: "blocking",
  }
}

import { Fragment, useEffect, useState } from "react"
import { Dialog, Disclosure, Menu, Popover, Tab, Transition } from "@headlessui/react"
import {
  Bars3Icon,
  MagnifyingGlassIcon,
  ShoppingBagIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline"
import {
  ChevronDownIcon,
  FunnelIcon,
  MinusIcon,
  PlusIcon,
  Squares2X2Icon,
} from "@heroicons/react/20/solid"
import Link from "next/link"

export type ShowCategoryProps = {
  children: React.ReactNode
  collection: any
  collections: any
  category: any
}

type CollectionListItem = {
  [key: string]: any
  parent_id: string | null
  top_id: null
  children: any[]
  sort: number
  slug: string
  description: string
}

const CollectionMenuItem = ({
  collection,
  closeMobileMenuOnClick,
  level = 0,
}: {
  collection: CollectionListItem
  closeMobileMenuOnClick: any
  level: number
}) => {
  const hasChildren = collection.children.length > 0
  const router = useRouter()
  const { handle } = router.query
  const hasMatchingChildSlug = (
    collection: CollectionListItem,
    handle: string | string[] | undefined
  ): boolean => {
    if (collection.children.length === 0) return false

    return collection.children.some(
      (child) => child.slug === handle || hasMatchingChildSlug(child, handle)
    )
  }
  const shouldOpen = collection.slug === handle || hasMatchingChildSlug(collection, handle)

  return (
    <Disclosure
      as="div"
      key={collection.id}
      className={classNames(level === 0 ? "border-t border-gray-200 py-6" : "", "px-4")}
      defaultOpen={shouldOpen}
    >
      {({ open }) => (
        <>
          <h3 className="-mx-2 -my-3 flow-root">
            {hasChildren ? (
              <Disclosure.Button className="flex w-full items-center justify-between bg-white px-2 py-3 hover:text-gray-500">
                <Disclosure.Button
                  as={Link}
                  className={classNames(
                    "font-medium text-gray-900",
                    handle === collection.slug ? "text-primary-900 font-bold underline" : ""
                  )}
                  href={`/collection/${collection.slug}`}
                >
                  {collection.name}
                </Disclosure.Button>
                <span className="ml-6 flex items-center">
                  {open ? (
                    <MinusIcon className="h-5 w-5" aria-hidden="true" />
                  ) : (
                    <PlusIcon className="h-5 w-5" aria-hidden="true" />
                  )}
                </span>
              </Disclosure.Button>
            ) : (
              <div className="flex w-full items-center justify-between bg-white px-2 py-3 hover:text-gray-500">
                <Disclosure.Button
                  as={Link}
                  className={classNames(
                    "font-medium text-gray-900",
                    handle === collection.slug ? "text-primary-900 font-bold underline" : ""
                  )}
                  href={`/collection/${collection.slug}`}
                  onClick={closeMobileMenuOnClick}
                >
                  {collection.name}
                </Disclosure.Button>
              </div>
            )}
          </h3>
          {hasChildren && (
            <Disclosure.Panel className="pt-6">
              <div className="space-y-6">
                {collection.children.map((childCollection) => (
                  <CollectionMenuItem
                    key={childCollection.id}
                    collection={childCollection}
                    closeMobileMenuOnClick={closeMobileMenuOnClick}
                    level={level + 1}
                  />
                ))}
              </div>
            </Disclosure.Panel>
          )}
        </>
      )}
    </Disclosure>
  )
}

const renderCollectionItem = (
  handle: string | string[] | undefined,
  collection: CollectionListItem,
  level: number = 0
) => {
  const hasChildren = collection.children && collection.children.length > 0
  const paddingLeft = level * 20 // Adjust the multiplier to control the indentation

  const hasMatchingChildSlug = (
    collection: CollectionListItem,
    handle: string | string[] | undefined
  ): boolean => {
    if (collection.children.length === 0) return false

    return collection.children.some(
      (child) => child.slug === handle || hasMatchingChildSlug(child, handle)
    )
  }
  const shouldOpen = collection.slug === handle || hasMatchingChildSlug(collection, handle)

  return (
    <Disclosure
      as="div"
      key={collection.id}
      className={classNames(level === 0 ? "border-b border-gray-200 py-6" : "", "")}
      defaultOpen={shouldOpen}
    >
      {({ open }) => (
        <>
          <h3 className="-my-3 flow-root">
            {hasChildren ? (
              <Disclosure.Button
                className="flex w-full items-center justify-between bg-white py-3 text-sm hover:text-gray-500"
                style={{ paddingLeft: `${paddingLeft}px` }}
              >
                <Disclosure.Button
                  as={Link}
                  className={classNames(
                    "font-medium text-gray-900",
                    handle === collection.slug ? "text-primary-900 font-bold underline" : ""
                  )}
                  href={`/collection/${collection.slug}`}
                >
                  {collection.name}
                </Disclosure.Button>
                <span className="ml-6 flex items-center">
                  {open ? (
                    <MinusIcon className="h-5 w-5" aria-hidden="true" />
                  ) : (
                    <PlusIcon className="h-5 w-5" aria-hidden="true" />
                  )}
                </span>
              </Disclosure.Button>
            ) : (
              <div
                className="flex w-full items-center justify-between bg-white py-3 text-sm hover:text-gray-500"
                style={{ paddingLeft: `${paddingLeft}px` }}
              >
                <Link
                  className={classNames(
                    "font-medium text-gray-900",
                    handle === collection.slug ? "text-primary-900 font-bold underline" : ""
                  )}
                  href={`/collection/${collection.slug}`}
                  legacyBehavior
                >
                  {collection.name}
                </Link>
              </div>
            )}
          </h3>
          {hasChildren && (
            <Disclosure.Panel className="pt-6">
              <div className="space-y-4">
                {collection.children.map((childCollection: CollectionListItem) =>
                  renderCollectionItem(handle, childCollection, level + 1)
                )}
              </div>
            </Disclosure.Panel>
          )}
        </>
      )}
    </Disclosure>
  )
}
function mapCollectionsToHierarchy(collections: CollectionListItem[]) {
  // Create a map of collections with their id as the key
  const collectionMap = collections.reduce((acc: any, collection) => {
    acc[collection.id] = { ...collection, children: [] }
    return acc
  }, {})

  // Create the hierarchical structure
  const hierarchy: CollectionListItem[] = []
  collections.forEach((collection) => {
    if (collection.parent_id === null) {
      // Top-level collection
      hierarchy.push(collectionMap[collection.id])
    } else {
      // Child collection
      const parent = collectionMap[collection.parent_id]
      if (parent) {
        parent.children.push(collectionMap[collection.id])
      }
    }
  })

  return hierarchy
}
const ShowCategory = ({ children, collection, collections, category }: ShowCategoryProps) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false)
  const router = useRouter()
  const { handle } = router.query
  const hierarchicalCollections = mapCollectionsToHierarchy(collections)

  const closeMobileMenuOnClick = (e: any) => {
    setMobileFiltersOpen(false)
    router.push(e.target.href)
  }
  return (
    <div className="bg-white">
      <div>
        {/* filter dialog */}
        <Transition.Root show={mobileFiltersOpen} as={Fragment}>
          <Dialog as="div" className="relative z-40 lg:hidden" onClose={setMobileFiltersOpen}>
            <Transition.Child
              as={Fragment}
              enter="transition-opacity ease-linear duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="transition-opacity ease-linear duration-300"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black bg-opacity-25" />
            </Transition.Child>

            <div className="fixed inset-0 z-40 flex">
              <Transition.Child
                as={Fragment}
                enter="transition ease-in-out duration-300 transform"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transition ease-in-out duration-300 transform"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <Dialog.Panel className="relative ml-auto flex h-full w-full max-w-xs flex-col overflow-y-auto bg-white py-4 pb-12 shadow-xl">
                  <div className="flex items-center justify-between px-4">
                    <h2 className="text-lg font-medium text-gray-900">Categories</h2>
                    <button
                      type="button"
                      className="-mr-2 flex h-10 w-10 items-center justify-center rounded-md bg-white p-2"
                      onClick={() => setMobileFiltersOpen(false)}
                    >
                      <span className="sr-only">Close menu</span>
                      <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                    </button>
                  </div>

                  {/* Filters */}
                  <form className="mt-4 border-t border-gray-200">
                    <h3 className="sr-only">Categories</h3>

                    {hierarchicalCollections.map((collection: CollectionListItem) => (
                      <CollectionMenuItem
                        key={collection.id}
                        collection={collection}
                        closeMobileMenuOnClick={closeMobileMenuOnClick}
                        level={0}
                      />
                    ))}
                  </form>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </Dialog>
        </Transition.Root>

        <main className="mx-auto max-w-full px-4 sm:px-6 lg:mx-14 mt-16 sm:mt-20">
          <div className="flex items-baseline justify-between border-b border-gray-200 pb-6 pt-8">
            <div className="w-full">
              {category.images[0] && (
                <div className="aspect-h-1 aspect-w-5 overflow-hidden rounded-lg">
                  <Image
                    src={category.images[0].file.url}
                    alt="category"
                    className="object-cover"
                    fill={true}
                  />
                </div>
              )}
              <h1 className="mt-4 text-4xl font-bold tracking-tight text-gray-900">
                {category.name}
              </h1>

              <p
                className="mt-4 text-base text-gray-500"
                dangerouslySetInnerHTML={{ __html: category.description }}
              ></p>
            </div>
          </div>

          <section aria-labelledby="products-heading" className="relative pb-24 pt-6">
            <h2 id="products-heading" className="sr-only">
              Collections
            </h2>
            <div className="sticky top-4 z-10 flex flex-row-reverse">
              <button
                type="button"
                className="p-2 text-gray-400 hover:text-gray-500 sm:ml-6 lg:hidden"
                onClick={() => setMobileFiltersOpen(true)}
              >
                <div className="flex flex-col items-center">
                  <span className="sr-only">Categories</span>
                  <FunnelIcon className="h-5 w-5" aria-hidden="true" />
                  <span className="text-xs">Categories</span>
                </div>
              </button>
            </div>

            <div className="grid grid-cols-1 gap-x-8 gap-y-10 lg:grid-cols-5">
              <form className="relative hidden lg:block">
                <h3 className="sr-only">Categories</h3>
                <div className="sticky top-0">
                  {hierarchicalCollections.map((collection: CollectionListItem) =>
                    renderCollectionItem(handle, collection)
                  )}
                </div>
              </form>
              <div className="col-span-4">{children}</div>
            </div>
          </section>
        </main>
      </div>
    </div>
  )
}

export default function Handle({
  collection,
  collections,
  category,
  page,
}: InferGetStaticPropsType<typeof getStaticProps>) {
  const router = useRouter()
  const isLive = !Builder.isEditing && !Builder.isPreviewing
  const { theme } = useThemeUI()
  if (!collection && isLive) {
    return (
      <>
        <Head>
          <meta name="robots" content="noindex" />
          <meta name="title"></meta>
        </Head>
        <DefaultErrorPage statusCode={404} />
      </>
    )
  }

  return router.isFallback && isLive ? (
    <h1>Loading...</h1> // TODO (BC) Add Skeleton Views
  ) : (
    <ShowCategory collection={collection} collections={collections} category={category}>
      <BuilderComponent
        isStatic
        key={collection.id}
        model={builderModel}
        data={{ collection, theme, page: router.query.page, category }}
        {...(page && { content: page })}
      />
    </ShowCategory>
  )
}

Handle.Layout = Layout
